import { useCallback, useMemo, useState } from "react";
import { arrayMove } from "@dnd-kit/sortable";
import {
	findContainer,
	ROOT_DROP_ZONE_ID,
	ROOT_START_DROP_ZONE_ID,
	ROOT_END_DROP_ZONE_ID,
	ROOT_PARENT,
} from "../utilities/dragUtils";

/** @typedef {import("../types").InfoNode} InfoNode */
/** @typedef {import("../types").InfoNodeItemsMap} InfoNodeItemsMap */
/** @typedef {import("../types").InfoNodeMap} InfoNodeMap */

/**
 * Encapsulates drag state and handlers for the information page drag and drop functionality
 * @param {{
 *   items: InfoNodeItemsMap,
 *   nodesById: InfoNodeMap,
 *   auth: any,
 *   reorderInfoNodes: Function,
 *   setOptimisticTree?: Function,
 *   clearOptimisticTree?: Function,
 *   onReorderError?: (error: Error) => void,
 * }} params
 */
export function useDragState({
	items,
	nodesById,
	auth,
	reorderInfoNodes,
	setOptimisticTree,
	clearOptimisticTree,
	onReorderError,
}) {
	const [activeId, setActiveId] = useState(null);
	const [overId, setOverId] = useState(null);
	const [isSaving, setIsSaving] = useState(false);

	/**
	 * Clears all drag state
	 */
	const resetDragState = useCallback(() => {
		setActiveId(null);
		setOverId(null);
	}, []);

	/**
	 * Captures the node that started dragging plus the current drop target.
	 * @param {{ active: { id: string }, over?: { id: string } }} event
	 */
	const handleDragStart = useCallback((event) => {
		setActiveId(event.active.id);
		setOverId(event.over ? event.over.id : null);
	}, []);

	/**
	 * Keeps track of the latest drop target as the pointer moves.
	 * @param {{ over?: { id: string } }} event
	 */
	const handleDragMove = useCallback((event) => {
		setOverId(event.over ? event.over.id : null);
	}, []);

	/**
	 * Cancels the drag interaction and resets state.
	 */
	const handleDragCancel = useCallback(() => {
		resetDragState();
	}, [resetDragState]);

	/**
	 * Applies optimistic reorder updates, persists them, and handles failure states.
	 * @param {{ active: { id: string }, over?: { id: string } }} event
	 */
	const handleDragEnd = useCallback(
		async (event) => {
			const { active, over } = event;

			// Cancel if dropped in the void
			if (!over) {
				resetDragState();
				return;
			}

			const activeNode = nodesById.get(active.id);
			if (!activeNode || active.id === over.id) {
				resetDragState();
				return;
			}

			const overData = over.data ? over.data.current : null;
			const isRootDropZone = over.id === ROOT_DROP_ZONE_ID;
			const isRootStartDropZone = over.id === ROOT_START_DROP_ZONE_ID;
			const isRootEndDropZone = over.id === ROOT_END_DROP_ZONE_ID;
			const activeContainerId =
				activeNode.type === "folder"
					? ROOT_PARENT
					: findContainer(items, active.id);

			// Rule: Only nested page can be dropped in the "drop out of folder" zone.
			if (
				isRootDropZone &&
				(activeNode.type !== "page" || activeContainerId === ROOT_PARENT)
			) {
				resetDragState();
				return;
			}

			// Rule: Only non-nested items can be dropped in the "move to start/end" zones.
			if (isRootStartDropZone && activeNode.parentId !== ROOT_PARENT) {
				resetDragState();
				return;
			}
			if (isRootEndDropZone && activeNode.parentId !== ROOT_PARENT) {
				resetDragState();
				return;
			}

			let overContainerId;

			if (overData && overData.nodeType === "folder-nest-target") {
				// Dropping over the folder droppable area => target folder id
				overContainerId = overData.containerId;
			} else if (
				// Active: Page / Over: Empty folder => target: parent folder id
				overData &&
				overData.nodeType === "folder" &&
				overData.containerId &&
				activeNode.type === "page" &&
				Array.isArray(items[overData.containerId]) &&
				items[overData.containerId].length === 0
			) {
				overContainerId = overData.containerId;
			} else if (overData && overData.parentId) {
				// Active: Page / Over: Nested pages => target: parent folder id
				overContainerId = overData.parentId;
			} else if (isRootDropZone || isRootStartDropZone || isRootEndDropZone) {
				// Root zone => target: root zone
				overContainerId = ROOT_PARENT;
			} else {
				// Otherwise, get the parent
				overContainerId = findContainer(items, over.id);
				if (!overContainerId && items.hasOwnProperty(over.id)) {
					overContainerId = over.id;
				}
			}

			// Rule: folders can only live at root level
			if (activeNode.type === "folder" && overContainerId !== ROOT_PARENT) {
				resetDragState();
				return;
			}

			const activeItems = [...(items[activeContainerId] || [])];
			const overItems =
				activeContainerId === overContainerId
					? activeItems
					: [...(items[overContainerId] || [])];
			const activeIndex = activeItems.indexOf(active.id);
			if (activeIndex === -1) {
				resetDragState();
				return;
			}

			// Handle root drop zone indexes
			let overIndex = overItems.indexOf(over.id);
			if (overIndex === -1) {
				if (isRootDropZone || isRootStartDropZone) {
					overIndex = 0;
				} else {
					overIndex = overItems.length;
				}
			}

			const nextItems = { ...items };

			const isDroppedInSameContainer = activeContainerId === overContainerId;
			if (isDroppedInSameContainer) {
				// Simply move within the container
				nextItems[activeContainerId] = arrayMove(
					activeItems,
					activeIndex,
					overIndex,
				);
			} else {
				// Otherwise, take it out of the container and add to the other container
				const [movedItem] = activeItems.splice(activeIndex, 1);
				overItems.splice(overIndex, 0, movedItem);
				nextItems[activeContainerId] = activeItems;
				nextItems[overContainerId] = overItems;
			}

			// Flatten the tree structure for the API
			const flattenedOrder = [];
			const rootItems = nextItems[ROOT_PARENT] || [];
			for (const nodeId of rootItems) {
				flattenedOrder.push({ nodeId, parentId: ROOT_PARENT });
				if (nextItems.hasOwnProperty(nodeId)) {
					for (const childId of nextItems[nodeId]) {
						flattenedOrder.push({ nodeId: childId, parentId: nodeId });
					}
				}
			}
			const payload = flattenedOrder
				.map((entry, index) => {
					const node = nodesById.get(entry.nodeId);
					if (!node) {
						return null;
					}
					return {
						...node,
						order: index,
						parentId: entry.parentId,
					};
				})
				.filter(Boolean);

			// Rebuild the indexes
			const updatedNodesById = new Map(nodesById);
			const updatedNormalizedNodes = [];
			payload.forEach((entry) => {
				updatedNodesById.set(entry.nodeId, entry);
				updatedNormalizedNodes.push(entry);
			});

			// Finally, apply those changes
			setOptimisticTree({
				items: nextItems,
				nodesById: updatedNodesById,
				normalizedNodes: updatedNormalizedNodes,
			});
			resetDragState();
			setIsSaving(true);
			try {
				await reorderInfoNodes(auth.site, payload);
			} catch (error) {
				clearOptimisticTree();
				if (typeof onReorderError === "function") {
					onReorderError(error);
				}
			} finally {
				setIsSaving(false);
			}
		},
		[
			auth,
			items,
			nodesById,
			reorderInfoNodes,
			resetDragState,
			setOptimisticTree,
			clearOptimisticTree,
			onReorderError,
		],
	);

	return useMemo(
		() => ({
			activeId,
			overId,
			isSaving,
			handleDragStart,
			handleDragMove,
			handleDragEnd,
			handleDragCancel,
		}),
		[
			activeId,
			overId,
			isSaving,
			handleDragStart,
			handleDragMove,
			handleDragEnd,
			handleDragCancel,
		],
	);
}
